import React, { useState, useCallback, useContext, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import { connect } from 'react-redux';
import { ThemeContext } from 'styled-components';
import { sendViewPurchaseOptionTooltipEvent } from 'snowplow';

import Tooltip from 'ggApp/shared/containers/tooltip';
import { PureTranslator } from 'ggApp/modules/translator/components';
import If from 'ggApp/shared/components/Conditional';
import { formatPriceByLanguage } from 'ggApp/utils/money';
import { CURRENCIES_ASSOCIATION } from 'ggApp/modules/store/constants';
import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import { ItemProps } from 'ggApp/shared/context/Order/helpers/orderMapper';
import { purchaseOptionTestFeatureFlagData as purchaseOptionTestFeatureFlagDataSelector } from 'ggApp/modules/featureFlag/selectors';
import {
    currentLanguage as currentLanguageSelector,
    storeActive as storeActiveSelector,
} from 'ggApp/modules/store/selectors';

import { PurchaseOptionWrap, Icon, IconWrapper } from './styles';

type PurchaseOptionKeys = { A: string; B: string; C: string };

const tooltipTranslationKeymap: PurchaseOptionKeys = {
    A: 'PURCHASE_OPTION_TOOLTIP_STRAIGHT_PRICE',
    B: 'PURCHASE_OPTION_TOOLTIP_MONTHS_TO_OWN',
    C: 'PURCHASE_OPTION_TOOLTIP_MONTHS_AFTER_RRP',
};

const learnMoreUrlKeymap: PurchaseOptionKeys = {
    A: 'http://help.grover.com/articles/5126931-how-does-the-purchase-option-work-purchase-price',
    B: 'http://help.grover.com/articles/5126922-how-does-the-purchase-option-work-months-to-own',
    C: 'http://help.grover.com/articles/5126934-how-does-the-purchase-option-work-months-after-rrp',
};

interface SelectorProps {
    locale: string;
    storeActive: { code: string };
    purchaseOptionTestFeatureFlagData: {
        variantKey: 'A' | 'B' | 'C';
    };
}

interface PTooltipProps {
    item?: ItemProps;
    userId: string;
    userDevice?: string;
    orderNumber: string;
}

export const PTooltip = ({
    item,
    userDevice,
    orderNumber,
    locale,
    storeActive: { code },
    purchaseOptionTestFeatureFlagData,
}: PTooltipProps & SelectorProps): JSX.Element | null => {
    const theme = useContext(ThemeContext);
    const { activeStore } = useContext(OrderContext);

    const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
    const checkoutModal = document.getElementsByClassName('checkoutModalContent')[0];

    const { buyoutMonths, variant, purchaseOption } = item || {};
    const variantSKU = variant?.sku ?? null;
    const productSKU = variant?.product?.sku ?? null;
    const productCategory = variant?.product?.category?.categoryId ?? null;
    const productSubCategory = variant?.product?.category?.subCategoryId ?? null;
    const purchaseOptionBadgeType = purchaseOptionTestFeatureFlagData?.variantKey;
    const tooltipTranslationKey = tooltipTranslationKeymap[purchaseOptionBadgeType];
    const currency = CURRENCIES_ASSOCIATION[activeStore.default_currency];

    const withValues = useMemo(() => {
        if (!activeStore || !isEmpty(purchaseOption.monthsAfterRrp)) {
            return null;
        }

        const values = { learnMoreUrl: learnMoreUrlKeymap[purchaseOptionBadgeType] };

        if (purchaseOptionBadgeType === 'A') {
            const formattedPrice = formatPriceByLanguage(
                purchaseOption.straightPrice,
                currency,
                activeStore.activeLanguage,
            );

            return { ...values, price: formattedPrice };
        }

        if (purchaseOptionBadgeType === 'B') {
            return { ...values, months: purchaseOption.monthsRequired };
        }

        const formattedPrice = formatPriceByLanguage(
            purchaseOption.marketPrice,
            currency,
            activeStore.activeLanguage,
        );

        return {
            ...values,
            months: purchaseOption.monthsAfterRrp,
            retailPrice: formattedPrice,
            currency,
        };
    }, [purchaseOptionBadgeType, activeStore, purchaseOption, currency]);

    const trackTooltip = useCallback(() => {
        sendViewPurchaseOptionTooltipEvent({
            eventCategory: 'checkout',
            eventAction: 'tooltip',
            eventLabel: 'mietKauf',
            eventProperty: {
                locale,
                store: code,
                location: 'checkout',
                product_sku: productSKU,
                variant_sku: variantSKU,
                category: productCategory,
                sub_category: productSubCategory,
                current_url: window.location.href,
                step: 'order_review',
                order_id: orderNumber,
            },
        });
    }, [code, locale, orderNumber, variantSKU, productSKU, productCategory, productSubCategory]);

    const showTooltip = useCallback(() => {
        setTooltipOpen(true);
    }, [setTooltipOpen]);

    const hideTooltip = useCallback(() => {
        setTooltipOpen(false);
        checkoutModal.removeEventListener('scroll', hideTooltip);
        checkoutModal.removeEventListener('click', hideTooltip);
    }, [checkoutModal, setTooltipOpen]);

    const handleTooltip = useCallback(() => {
        checkoutModal.addEventListener('scroll', hideTooltip);
        checkoutModal.addEventListener('click', hideTooltip);
        trackTooltip();
    }, [checkoutModal, hideTooltip, trackTooltip]);

    if (!buyoutMonths || !isEmpty(purchaseOption.monthsAfterRrp)) {
        return null;
    }

    return (
        <PurchaseOptionWrap>
            <PureTranslator tk="KEEP_IT_FOREVER_BADGE_TEXT" />

            <If condition={userDevice === 'mobile'}>
                <div data-testid="mobile-purchase-option-tooltip">
                    <Tooltip
                        tooltipTK={
                            purchaseOptionBadgeType
                                ? tooltipTranslationKey
                                : 'RENT_TO_OWN_KEEP_IT_FOREVER_DESCRIPTION'
                        }
                        withValues={
                            purchaseOptionBadgeType
                                ? withValues
                                : {
                                      period: buyoutMonths + 1,
                                      currency,
                                  }
                        }
                        onShown={handleTooltip}
                        trigger="manual"
                        open={tooltipOpen}
                        data-testid="mobile-tooltip"
                    >
                        <IconWrapper
                            onClick={showTooltip}
                            role="button"
                            tabIndex={0}
                            onKeyPress={showTooltip}
                            onMouseOver={showTooltip}
                            onFocus={showTooltip}
                        >
                            <Icon color={theme.colors['Level 3']} />
                        </IconWrapper>
                    </Tooltip>
                </div>
            </If>

            <If condition={userDevice !== 'mobile'}>
                <div data-testid="desktop-purchase-option-tooltip">
                    <Tooltip
                        tooltipTK={
                            purchaseOptionBadgeType
                                ? tooltipTranslationKey
                                : 'RENT_TO_OWN_KEEP_IT_FOREVER_DESCRIPTION'
                        }
                        withValues={
                            purchaseOptionBadgeType
                                ? withValues
                                : {
                                      period: buyoutMonths + 1,
                                      currency,
                                  }
                        }
                        onShown={trackTooltip}
                        hideOnClick
                        data-testid="desktop-tooltip-icon"
                    >
                        <Icon color={theme.colors['Level 3']} />
                    </Tooltip>
                </div>
            </If>
        </PurchaseOptionWrap>
    );
};

const enhance = connect<SelectorProps, {}, PTooltipProps>((state, ownProps) => ({
    purchaseOptionTestFeatureFlagData: purchaseOptionTestFeatureFlagDataSelector(state),
    storeActive: storeActiveSelector(state, ownProps),
    locale: currentLanguageSelector(state),
}));

export const PurchaseTooltip = enhance(PTooltip);
